import { Fragment, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box } from '@interxfi/design-system/components/box/box';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Permission, type PermissionProps } from '@interxfi/design-system/components/permission/permission';
import { Typography } from '@interxfi/design-system/components/typography';
import styles from './card-summary-group.module.scss';

export type CardSummaryGroupHeaderVariant = 'default' | 'onNeutral';

interface CardSummaryItemReadonlyListItems {
  kind: 'list';
  id: string;
  items: ReadonlyListItem[];
}

interface CardSummaryItemPermission {
  kind: 'permission';
  id: string;
  item: Omit<PermissionProps, 'isPending'>;
}

interface CardSummaryItemNode {
  kind: 'node';
  id: string;
  node: ReactNode;
}

export type CardSummaryItem = CardSummaryItemReadonlyListItems | CardSummaryItemPermission | CardSummaryItemNode;

interface CardSummaryGroupProps {
  icon?: ReactNode;
  isPending: boolean;
  items: CardSummaryItem[];
  title: string;
  headerClassName?: string;
  headerVariant?: CardSummaryGroupHeaderVariant;
}

interface CardSummaryGroupBodyProps {
  isPending: boolean;
  items: CardSummaryItem[];
}

export function CardSummaryGroupBody({ isPending, items }: CardSummaryGroupBodyProps) {
  return (
    <VerticalStack className={styles.paddedContainer}>
      {items.map((item) => {
        switch (item.kind) {
          case 'list':
            return <AppReadonlyList isPending={isPending} items={item.items} key={item.id} />;
          case 'permission':
            return <Permission key={item.id} {...item.item} isPending={isPending} value={Boolean(item.item.value)} />;
          case 'node':
            return <Fragment key={item.id}>{item.node}</Fragment>;
          default:
            return null;
        }
      })}
    </VerticalStack>
  );
}

export function CardSummaryGroup({ icon, isPending, items, title, headerClassName, headerVariant }: CardSummaryGroupProps) {
  return (
    <Box borderRadius="8px" className={styles.cardSummaryGroup}>
      <VerticalStack>
        <HorizontalStack
          blockAlign="center"
          className={clsx(styles.header, headerVariant === 'onNeutral' && styles.headerOnNeutral, headerClassName)}
          gap={2}
          wrap="nowrap"
        >
          {icon}

          <Typography lineHeight="1.5rem" size="0.875rem">
            {title}
          </Typography>
        </HorizontalStack>

        <CardSummaryGroupBody isPending={isPending} items={items} />
      </VerticalStack>
    </Box>
  );
}
